import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/useResponsive";
import { cn } from "@/lib/utils";
import { Theme, ThemeContent } from "@/types/theme.types";

interface ThemeCardWithSelectionProps {
  theme: Theme;
  isSelected: boolean;
  onToggle?: (themeId: string) => void;
  onEdit?: (theme: Theme) => void;
}

// Key colors to display in preview (in order)
const KEY_COLORS = [
  { key: "background", label: "BG" },
  { key: "foreground", label: "FG" },
  { key: "primary", label: "Pri" },
  { key: "secondary", label: "Sec" },
  { key: "accent", label: "Acc" },
  { key: "muted", label: "Mut" },
] as const;

// Convert color string to CSS format
// Handles: hsl(), oklch(), rgb(), hex, or raw "h s% l%" values
function colorToCss(color: string): string {
  if (!color) return "transparent";
  // Already has a CSS function wrapper (hsl, oklch, rgb, etc.)
  if (
    color.startsWith("hsl") ||
    color.startsWith("oklch") ||
    color.startsWith("rgb") ||
    color.startsWith("#")
  ) {
    return color;
  }
  // Raw HSL values like "213 94% 68%" - wrap with hsl()
  return `hsl(${color})`;
}

function ThemePreviewTooltip({ theme }: { theme: Theme }): JSX.Element {
  const themeContent = useMemo(() => {
    try {
      return JSON.parse(theme.content) as ThemeContent;
    } catch {
      return null;
    }
  }, [theme.content]);

  if (!themeContent) {
    // Fallback to simple 4-color preview
    return (
      <div className="p-3 min-w-[180px]">
        <div className="grid grid-cols-2 gap-2 mb-3">
          {theme.colors.slice(0, 4).map((color, i) => (
            <div
              key={i}
              className="w-16 h-16 rounded-lg border border-border/40 shadow-sm"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <div className="space-y-1">
          <h3 className="font-semibold text-sm">{theme.name}</h3>
          <Badge
            variant={theme.type === "built-in" ? "secondary" : "default"}
            className="text-[10px] h-5 px-2"
          >
            {theme.type === "built-in" ? "Built-in" : "Custom"}
          </Badge>
        </div>
      </div>
    );
  }

  const { light, dark } = themeContent.cssVars;

  return (
    <div className="p-4 min-w-[280px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm">{theme.name}</h3>
        <Badge
          variant={theme.type === "built-in" ? "secondary" : "default"}
          className="text-[10px] h-5 px-2"
        >
          {theme.type === "built-in" ? "Built-in" : "Custom"}
        </Badge>
      </div>

      {/* Light Mode Colors */}
      <div className="mb-3">
        <p className="text-xs text-muted-foreground mb-2 font-medium">
          Light Mode
        </p>
        <div className="grid grid-cols-6 gap-1.5">
          {KEY_COLORS.map(({ key, label }) => (
            <div key={key} className="flex flex-col items-center gap-1">
              <div
                className="w-9 h-9 rounded-md border border-border/50 shadow-sm"
                style={{ backgroundColor: colorToCss(light[key]) }}
                title={`${label}: ${light[key]}`}
              />
              <span className="text-[9px] text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Dark Mode Colors */}
      <div>
        <p className="text-xs text-muted-foreground mb-2 font-medium">
          Dark Mode
        </p>
        <div className="grid grid-cols-6 gap-1.5">
          {KEY_COLORS.map(({ key, label }) => (
            <div key={key} className="flex flex-col items-center gap-1">
              <div
                className="w-9 h-9 rounded-md border border-border/50 shadow-sm"
                style={{ backgroundColor: colorToCss(dark[key]) }}
                title={`${label}: ${dark[key]}`}
              />
              <span className="text-[9px] text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ThemeCardWithSelection({
  theme,
  isSelected,
  onToggle,
  onEdit,
}: ThemeCardWithSelectionProps): JSX.Element {
  const isMobile = useIsMobile();
  const isCustom = theme.type === "custom";

  const handleClick = () => {
    // If onEdit is provided, clicking the card opens edit
    if (onEdit) {
      onEdit(theme);
    }
  };

  const cardContent = (
    <div
      onClick={handleClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        "relative p-3 rounded-lg transition-all duration-150",
        "cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        isSelected
          ? "border-2 border-primary bg-primary/10 shadow-md"
          : "border-2 border-transparent hover:border-primary/30 hover:shadow-sm",
      )}
    >
      {/* Checkbox - only for custom themes and when onToggle is provided (admin only) */}
      {isCustom && onToggle && (
        <div
          className={cn("absolute z-10", isMobile ? "top-1 left-1 p-1.5" : "top-2 left-2")}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggle(theme.themeId)}
            className={cn(
              "rounded-full border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground",
              isMobile ? "h-6 w-6" : "h-5 w-5",
            )}
          />
        </div>
      )}

      {/* Color swatches - horizontal layout */}
      <div className={cn("flex gap-1 mb-2", isCustom && onToggle && "ml-6")}>
        {theme.colors.slice(0, 4).map((color, i) => (
          <div
            key={i}
            className="w-6 h-6 rounded-md border border-border/40 shadow-sm"
            style={{ backgroundColor: color }}
          />
        ))}
      </div>

      {/* Theme name and type */}
      <div className="min-w-0">
        <h3 className="font-medium text-xs mb-1 truncate" title={theme.name}>
          {theme.name}
        </h3>
        <Badge variant="outline" className="text-[9px] h-4 px-1.5">
          {theme.type === "built-in" ? "Built-in" : "Custom"}
        </Badge>
      </div>
    </div>
  );

  if (isMobile) {
    return cardContent;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="start"
          className="bg-popover text-popover-foreground border shadow-lg p-0"
        >
          <ThemePreviewTooltip theme={theme} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
